document.addEventListener('alpine:init', () => {
  const init = window.__cartInit__ || { itemCount: 0, totalPrice: 0, items: [] };

  Alpine.store('cart', {
    open:    false,
    loading: false,   // add-to-cart in flight (disables product page button)
    syncing: false,   // qty sync in flight (shows drawer loading bar)
    toast:   null,

    items:      init.items,
    itemCount:  init.itemCount,
    totalPrice: init.totalPrice,

    // Optimistic qty overrides: { [key]: number }
    localQty:     {},
    // Debounce timers per item key: { [key]: timerId }
    _timers:      {},
    // AbortControllers for in-flight qty requests: { [key]: AbortController }
    _controllers: {},
    _toastTimer:  null,

    // ── Helpers ─────────────────────────────────────────

    getQty(item) {
      return this.localQty[item.key] ?? item.quantity;
    },

    get optimisticTotal() {
      return this.items.reduce((sum, item) => {
        return sum + item.price * (this.localQty[item.key] ?? item.quantity);
      }, 0);
    },

    money(cents) {
      return 'NT$' + Math.round(cents / 100);
    },

    showToast(msg) {
      this.toast = msg;
      if (this._toastTimer) clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(() => { this.toast = null; }, 3500);
    },

    async refresh() {
      const res  = await fetch('/cart.js');
      const data = await res.json();
      this.items      = data.items;
      this.itemCount  = data.item_count;
      this.totalPrice = data.total_price;
    },

    // ── Add to cart (product page) ───────────────────────

    async addItem(formEl) {
      const id  = formEl.querySelector('[name="id"]').value;
      const qty = parseInt(formEl.querySelector('[name="quantity"]')?.value || 1);
      this.loading = true;
      try {
        const res = await fetch('/cart/add.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ id, quantity: qty }),
        });
        if (!res.ok) throw new Error();
        await this.refresh();
        this.open = true;
      } catch {
        this.showToast('加入購物車失敗，請重試');
      } finally {
        this.loading = false;
      }
    },

    // ── Qty ± (optimistic display, debounced + abortable API) ──

    adjustQty(key, delta) {
      const item = this.items.find(i => i.key === key);
      if (!item) return;

      const current = this.localQty[key] ?? item.quantity;
      const next    = Math.max(0, current + delta);

      if (next === 0) { this.remove(key); return; }

      // Update display immediately (optimistic)
      this.localQty[key] = next;

      // Cancel pending timer AND abort any in-flight request for this key
      if (this._timers[key]) clearTimeout(this._timers[key]);
      if (this._controllers[key]) {
        this._controllers[key].abort();
        delete this._controllers[key];
      }

      // Schedule API call 1s after last interaction
      this._timers[key] = setTimeout(() => {
        delete this._timers[key];
        this._syncQty(key, next);
      }, 1000);
    },

    async _syncQty(key, qty) {
      const serverQty  = this.items.find(i => i.key === key)?.quantity;
      const controller = new AbortController();
      this._controllers[key] = controller;

      this.syncing = true;
      try {
        const res = await fetch('/cart/change.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ id: key, quantity: qty }),
          signal:  controller.signal,
        });
        if (!res.ok) throw new Error('failed');
        delete this._controllers[key];
        await this.refresh();
        delete this.localQty[key];
      } catch (e) {
        if (e.name === 'AbortError') return; // superseded by a newer request — ignore
        // Roll back optimistic qty to last confirmed server value
        if (serverQty !== undefined) {
          this.localQty[key] = serverQty;
        } else {
          delete this.localQty[key];
        }
        this.showToast('數量更新失敗，已恢復原本數量');
      } finally {
        this.syncing = false;
      }
    },

    // ── Remove item (optimistic, immediate, no debounce) ─

    async remove(key) {
      // Cancel pending timer and in-flight request for this key
      if (this._timers[key]) {
        clearTimeout(this._timers[key]);
        delete this._timers[key];
      }
      if (this._controllers[key]) {
        this._controllers[key].abort();
        delete this._controllers[key];
      }

      // Optimistically remove from list
      const removed  = this.items.find(i => i.key === key);
      this.items     = this.items.filter(i => i.key !== key);
      this.itemCount = Math.max(0, this.itemCount - (removed?.quantity ?? 1));
      delete this.localQty[key];

      this.syncing = true;
      try {
        const res = await fetch('/cart/change.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ id: key, quantity: 0 }),
        });
        if (!res.ok) throw new Error();
        await this.refresh();
      } catch {
        await this.refresh(); // restore actual server state
        this.showToast('移除失敗，請重試');
      } finally {
        this.syncing = false;
      }
    },
  });
});
